import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useLang, pick } from "@/context/LangContext";
import { CONTACT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, ShieldCheck, HandHeart, Wallet, HeadphonesIcon, Stethoscope, PlaneTakeoff, Building2, Star, Quote } from "lucide-react";
import InquiryForm from "@/components/site/InquiryForm";

const HERO_IMG = "https://images.unsplash.com/photo-1626315869436-d6781ba69d6e?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
  const { t, lang, isRTL } = useLang();
  const [treatments, setTreatments] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  useEffect(() => {
    axios.get(`${API}/treatments`).then((r) => setTreatments(r.data));
    axios.get(`${API}/hospitals`).then((r) => setHospitals(r.data));
    axios.get(`${API}/doctors`).then((r) => setDoctors(r.data));
    axios.get(`${API}/testimonials`).then((r) => setTestimonials(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-sage-900/85 via-sage-900/60 to-sage-900/10" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-24 pb-32 text-bone">
          <div className="max-w-3xl animate-fade-up">
            <div className="section-eyebrow text-ochre-400 mb-6">{t("hero.eyebrow")}</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05]">
              {t("hero.title")}
            </h1>
            <p className="mt-6 text-lg text-bone/85 leading-relaxed max-w-2xl">{t("hero.subtitle")}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" data-testid="hero-cta-consult">
                <Button className="rounded-full bg-ochre-500 hover:bg-ochre-600 h-12 px-7 text-base shadow-lg shadow-ochre-500/25">
                  {t("hero.cta_primary")} <Arrow className="ms-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/treatments" data-testid="hero-cta-treatments">
                <Button variant="outline" className="rounded-full h-12 px-7 text-base bg-transparent text-bone border-bone/40 hover:bg-bone/10 hover:text-bone">
                  {t("hero.cta_secondary")}
                </Button>
              </Link>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl">
            {[
              ["5,000+", t("hero.stat_patients")],
              ["25+", t("hero.stat_hospitals")],
              ["40+", t("hero.stat_countries")],
              ["70%", t("hero.stat_saved")],
            ].map(([n, l], i) => (
              <div key={i} className="animate-fade-up" style={{ animationDelay: `${i * 120}ms` }}>
                <div className="font-display text-3xl sm:text-4xl font-semibold text-ochre-400">{n}</div>
                <div className="text-xs uppercase tracking-widest opacity-70 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <section className="border-y border-stone-200 bg-white py-6 overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mb-3 flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{t("trust.title")}</div>
          <div className="hidden sm:block text-xs text-stone-500">{t("trust.subtitle")}</div>
        </div>
        <div className="overflow-hidden">
          <div className="flex gap-14 marquee-track whitespace-nowrap px-4 items-center">
            {[...hospitals, ...hospitals].map((h, i) => (
              <div key={i} className="flex items-center gap-3 text-sage-900">
                <Building2 className="h-5 w-5 text-ochre-500" />
                <span className="font-display text-lg font-medium">{pick(h.name, lang)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TREATMENTS BENTO */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex items-end justify-between gap-6 mb-12">
          <div>
            <div className="section-eyebrow">01 — {t("nav.treatments")}</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-ink max-w-xl">{t("treatments.title")}</h2>
            <p className="mt-3 text-stone-600 max-w-xl">{t("treatments.subtitle")}</p>
          </div>
          <Link to="/treatments" className="hidden md:inline-flex items-center gap-2 text-sage-900 font-medium hover:text-ochre-500">
            {t("treatments.view_all")} <Arrow className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[220px]">
          {treatments.slice(0, 8).map((tr, i) => {
            const large = i === 0 || i === 5;
            return (
              <Link
                to={`/treatments/${tr.slug}`}
                key={tr.slug}
                data-testid={`treatment-card-${tr.slug}`}
                className={`group relative overflow-hidden rounded-3xl border border-stone-200 bg-white transition-all hover:-translate-y-1 hover:shadow-lg ${large ? "md:col-span-2 md:row-span-2" : ""}`}
              >
                <img src={tr.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/30 to-transparent" />
                <div className="relative h-full p-5 flex flex-col justify-end text-bone">
                  <div className="text-[10px] uppercase tracking-widest text-ochre-400 font-semibold">{tr.category}</div>
                  <div className="font-display font-semibold text-lg sm:text-xl mt-1">{pick(tr.name, lang)}</div>
                  {large && (
                    <p className="text-sm opacity-85 mt-2 line-clamp-2 max-w-md">{pick(tr.short_desc, lang)}</p>
                  )}
                  <div className="mt-3 text-xs text-bone/80">
                    {t("treatments.from")} <span className="text-ochre-400 font-semibold">${tr.price_from_usd?.toLocaleString()}</span>
                    {tr.duration_days ? <span className="mx-2 opacity-60">·</span> : null}
                    {tr.duration_days && <>{t("treatments.duration")}: {tr.duration_days}</>}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-sage-50/60 border-y border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <div className="section-eyebrow">02 — {t("nav.about")}</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-ink">{t("why.title")}</h2>
            <p className="mt-3 text-stone-600">{t("why.subtitle")}</p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              [HandHeart, "why.p1_title", "why.p1_body"],
              [ShieldCheck, "why.p2_title", "why.p2_body"],
              [Wallet, "why.p3_title", "why.p3_body"],
              [HeadphonesIcon, "why.p4_title", "why.p4_body"],
            ].map(([Icon, tk, bk], i) => (
              <div key={i} className="bg-white rounded-3xl border border-stone-200 p-7 hover:-translate-y-1 hover:shadow-md transition-all">
                <div className="h-12 w-12 rounded-2xl bg-sage-100 text-sage-900 flex items-center justify-center">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="mt-5 font-display font-semibold text-lg text-ink">{t(tk)}</div>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed">{t(bk)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOSPITALS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex items-end justify-between gap-6 mb-12">
          <div>
            <div className="section-eyebrow">03 — {t("nav.hospitals")}</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-ink max-w-xl">{t("hospitals.title")}</h2>
            <p className="mt-3 text-stone-600 max-w-xl">{t("hospitals.subtitle")}</p>
          </div>
          <Link to="/hospitals" className="hidden md:inline-flex items-center gap-2 text-sage-900 font-medium hover:text-ochre-500">
            {t("hospitals.title")} <Arrow className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {hospitals.slice(0, 6).map((h) => (
            <Link to={`/hospitals/${h.id}`} key={h.id} data-testid={`hospital-card-${h.id}`} className="group bg-white rounded-3xl border border-stone-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={h.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <div className="text-xs uppercase tracking-widest text-ochre-500 font-semibold">{h.city}</div>
                <div className="font-display font-semibold text-lg text-ink mt-1">{pick(h.name, lang)}</div>
                <p className="mt-2 text-sm text-stone-600 line-clamp-2">{pick(h.description, lang)}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {(h.accreditations || []).map((a) => (
                    <span key={a} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-sage-100 text-sage-900 font-semibold">{a}</span>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between text-xs text-stone-500">
                  <span>{h.beds} {t("hospitals.beds")}</span>
                  <span>{t("hospitals.established")} {h.established}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* DOCTORS */}
      <section className="bg-white border-y border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <div className="section-eyebrow">04 — {t("nav.doctors")}</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-ink max-w-xl">{t("doctors.title")}</h2>
          <p className="mt-3 text-stone-600 max-w-xl">{t("doctors.subtitle")}</p>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {doctors.slice(0, 6).map((d) => (
              <div key={d.id} data-testid={`doctor-card-${d.id}`} className="flex gap-4 bg-bone rounded-3xl border border-stone-200 p-5">
                <div className="w-24 h-24 rounded-2xl overflow-hidden bg-stone-100 flex-shrink-0">
                  <img src={d.image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-semibold text-ink truncate">{pick(d.name, lang)}</div>
                  <div className="text-sm text-ochre-600 mt-0.5">{pick(d.specialty, lang)}</div>
                  <div className="text-xs text-stone-500 mt-1">{d.hospital}</div>
                  <div className="text-xs text-sage-900 mt-2 font-medium">{d.experience_years}+ {t("doctors.years_exp")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
        <div className="section-eyebrow">05 — {t("nav.about")}</div>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-ink max-w-xl">{t("testimonials.title")}</h2>
        <p className="mt-3 text-stone-600 max-w-xl">{t("testimonials.subtitle")}</p>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {testimonials.map((tst) => (
            <div key={tst.id} data-testid={`testimonial-${tst.id}`} className="bg-white rounded-3xl border border-stone-200 p-8">
              <Quote className="h-8 w-8 text-ochre-500" />
              <p className="mt-4 text-stone-700 leading-relaxed text-sm">"{pick(tst.quote, lang)}"</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full overflow-hidden bg-stone-100">
                  {tst.image && <img src={tst.image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div>
                  <div className="text-sm font-semibold text-ink">{tst.patient_name}</div>
                  <div className="text-xs text-stone-500">{tst.country} · {tst.treatment}</div>
                </div>
                <div className="ms-auto flex text-ochre-500">
                  {Array.from({ length: tst.rating }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-24">
        <div className="rounded-3xl bg-sage-900 text-bone p-10 sm:p-16 grid lg:grid-cols-2 gap-10 items-center overflow-hidden relative">
          <div>
            <div className="section-eyebrow text-ochre-400">06 — {t("nav.consult")}</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-bone">{t("inquiry.title")}</h2>
            <p className="mt-3 text-bone/80 max-w-md">{t("inquiry.subtitle")}</p>
          </div>
          <div>
            <InquiryForm />
          </div>
        </div>
      </section>
    </div>
  );
}
